<script setup>
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Textarea from "@/Components/Textarea.vue";
import Select from "@/Components/Select.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderLink.vue";
import Button from "@/Components/Button.vue";
import Heading from "@/Components/Settings/Heading.vue";
import { Head, useForm, Link } from "@inertiajs/inertia-vue3";
import TD from "@/Components/Table/Element.vue";
import { Inertia } from "@inertiajs/inertia";
import Modal from "@/Components/Settings/Modal.vue";
import { ref, onMounted } from "vue";
const page_name = "Terminanfragen";

const canEdit = Inertia.page.props.auth.can["edit settings"];
const canDelete = Inertia.page.props.auth.can["delete settings"];
const obsElement = ref(null);
const allAppointments = ref(props.appointments.data || []);

const props = defineProps({
  errors: Object,
  appointments: Object,
});

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.isIntersecting && loadMoreAppointments(), {
      rootMargin: "-150px 0px 0px 0px",
    });
  });

  observer.observe(obsElement.value);
});

const modalData = ref({
  show: false,
  id: Number,
});

function showDeleteModal(id) {
  modalData.value.id = id;
  modalData.value.show = true;
}

function deleteAppointment(id) {
  Inertia.delete(
    "/einstellungen/widget/delete/" + id,
    {},
    {
      only: ["appointments"],
      onSuccess: () => {
        // console.log("success");
        allAppointments.value = allAppointments.value.filter(
          (appointment) => appointment.id !== id
        );
      },
    }
  );
  modalData.value.show = false;
}

function loadMoreAppointments() {
  if (
    props.appointments.length === 0 ||
    props.appointments.next_page_url === null
  ) {
    return;
  }

  //console.log(props.appointments);

  Inertia.get(
    props.appointments.next_page_url,
    {},
    {
      preserveState: true,
      preserveScroll: true,
      only: ["appointments"],
      onSuccess: () => {
        allAppointments.value = [
          ...allAppointments.value,
          ...props.appointments.data,
        ];
        window.history.replaceState(
          {},
          "Terminanfragen",
          "/einstellungen/widgetanfragen"
        );
      },
    }
  );
}
</script>

<template>
  <SettingsLayout>
    <!-- <Head title="Stammdaten - Grundeinstellungen" /> -->
    <Head :title="`${page_name} - Grundeinstellungen`" />

    <template #breadcrumb>{{ page_name }}</template>
    <template #controls>
      <!-- class="mr-3" -->
      <HeaderLink class="mr-3" href="/" type="close">Schließen</HeaderLink>
      <!-- <HeaderButton @click="submitForm()">Speichern</HeaderButton> -->
    </template>
    <Heading title="Terminanfragen über die Online-Terminvereinbarung" />

    <Modal :show="modalData.show" @close="modalData.show = false">
      <template #title>
        <div>Warnung</div>
      </template>
      <template #message>
        <!-- {{ modalData.message }} -->
        <p>
          Die Anfrage wird unwiderruflich gelöscht und eine Nachricht, dass der
          Termin abgelehnt wurde wird an den Patienten geschickt. Möchten Sie
          fortfahren?
        </p>
      </template>
      <template #actions>
        <div class="">
          <div class="flex gap-5 justify-center mt-8">
            <Button
              class="mt-auto"
              @click="
                () => {
                  modalData.show = false;
                }
              "
              >Abbrechen</Button
            >

            <Button
              type="button"
              :disabled="isLoading"
              class="mt-auto"
              @click="deleteAppointment(modalData.id)"
              >Fortfahren</Button
            >
          </div>
        </div>
      </template>
    </Modal>

    <p class="max-w-5xl text-sm text-oteoText">
      Hier sehen Sie alle Termine, die von Patienten direkt über Ihre Homepage
      gebucht wurden. Oben rechts im Kalender sehen Sie auch die Anzahl der
      gebuchten Termine im roten Kreis, so dass Sie immer informiert sind, wenn
      ein Patient einen Termin gebucht hat. Über die Aktion "Bearbeiten" können
      Sie den Termin in Ihren Kalender eintragen. Sie müssen nur noch einen
      freien Raum auswählen. Mit dem Button "Einzeltermin anlegen" wird der
      Termin gespeichert. Der Patient erhält eine Bestätigungsmail.
    </p>

    <div class="h-[500px] mt-12 overflow-y-scroll">
      <table class="min-w-full">
        <thead class="bg-white border-b">
          <tr class="sticky -top-px border-white border-t bg-white">
            <th
              scope="col"
              class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
            >
              Zeitraum
            </th>
            <th
              scope="col"
              class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
            >
              Behandlung<br />Behandler
            </th>

            <th
              scope="col"
              class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
            >
              Patient<br />Versicherung - Erstbesuch
            </th>

            <!-- <th
              scope="col"
              class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
            >
              Video
            </th>

            <th
              scope="col"
              class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
            >
              Schmerzen
            </th> -->

            <th
              scope="col"
              class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
            >
              Aktionen
            </th>
          </tr>
        </thead>
        <tbody class="">
          <tr
            v-for="(slot, index) in allAppointments"
            :key="slot.id"
            class="border-b"
            :class="{
              'bg-gray-100': index % 2 === 0,
            }"
          >
            <TD>
              {{ slot.dateString }}<br />
              {{ `${slot.startString} - ${slot.endString} Uhr` }}<br />
            </TD>
            <TD>
              {{ slot.type.name }}<br />
              {{ slot.practitioner.fullname }}
            </TD>
            <TD>
              {{ slot.fullname }}<br />
              <!--<a class="break-words" :href="`mailto:${ slot.email }`">{{ slot.email }}</a><br>
                            <a class="break-words" :href="`tel:${ slot.tel }`">{{ slot.tel }}</a><br>-->
              {{ slot.insurance
              }}{{ slot.is_first_visit ? " - Erstbesuch" : "" }}
            </TD>
            <!-- <TD>
              {{ slot.video ? "Ja" : "" }}
            </TD>
            <TD>
              {{ slot.pain ? "Ja" : "" }}
            </TD> -->
            <TD>
              <div v-if="canEdit" class="flex items-center text-oteo-300">
                <Link
                  class="mr-4"
                  :href="`/einstellungen/widget/erstellen/${slot.id}`"
                >
                  <i class="las la-angle-right"></i>bearbeiten
                </Link>
                <div
                  v-if="canDelete"
                  class="cursor-pointer"
                  @click="showDeleteModal(slot.id)"
                >
                  <i class="las la-times"></i>
                  löschen
                </div>
              </div>
            </TD>
          </tr>
        </tbody>
      </table>
      <div
        class="sticky bottom-0 h-10 w-full bg-gradient-to-t from-white"
      ></div>
      <div class="h-8 w-full bg-transparent" ref="obsElement"></div>
    </div>
  </SettingsLayout>
</template>
